/**
 * useSiteConfig — 站点配置 React Hook。
 *
 * 从 GET /api/config 读取 siteConfig（KV 存储 + 默认值兜底）。
 * 内存缓存：同 session 多次调用共享一次请求（siteConfig 极少变更）。
 *
 * 兜底：请求失败时返回 null，调用方用硬编码默认。
 */

import { useEffect, useState } from "react";
import { getConfig, type SiteConfig } from "../lib/api";

let cached: SiteConfig | null = null;
let inflight: Promise<SiteConfig | null> | null = null;

/** 加载站点配置（带模块级缓存） */
function loadSiteConfig(): Promise<SiteConfig | null> {
  if (cached) return Promise.resolve(cached);
  if (inflight) return inflight;
  inflight = getConfig()
    .then((c) => {
      cached = c.siteConfig;
      return cached;
    })
    .catch(() => null)
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

/** 获取站点配置，失败返回 null */
export function useSiteConfig(): { config: SiteConfig | null; loading: boolean } {
  const [config, setConfig] = useState<SiteConfig | null>(cached);
  const [loading, setLoading] = useState(!cached);

  useEffect(() => {
    if (cached) return;
    let cancelled = false;
    loadSiteConfig().then((c) => {
      if (!cancelled) {
        setConfig(c);
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return { config, loading };
}
